import type { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import axios from 'axios';
import throttle from 'lodash/throttle';
import { ref } from 'vue';
import { formatBet } from '@/components/myBets/betHelper';
import { formatBetBuilderResults, formatDynamicMarketResults, formatOutrightResults, formatResults } from '@/components/results/formatResult';
import { formatBet as formatFantasySelectionBet, formatMyBets as formatFantasySelectionMyBets, formatMyBetDetail } from '@/core/fantasySelection/api/fantasySelectionMyBetHelper';
import { UnexpectedResponseError } from '@/core/lib/apiError';
import { getTicketLiveScore } from '@/core/lib/betBuilderHelper';
import { parseISOString } from '@/core/lib/date';
import { getPolluxUrl } from '@/core/lib/url';
import { formatBetBuilderOrder, formatBetBuilderTicket } from '@/core/oddsApi/helpers/formatBetBuilder';
import { formatDynamicMarketOrder, formatDynamicMarketTicket } from '@/core/oddsApi/helpers/formatDynamicMarket';
import type { IMyBets } from '@/interface/IBet';
import type { IBetSlip, IBetSlipEuro, ITicket, ITicketEuro } from '@/interface/IBetSlip';
import type { ICashOutData } from '@/interface/ICashOut';
import type { IToggleResponse } from '@/interface/IToggle';
import type { IVoucher } from '@/interface/IVoucher';
import type { IBetBuilderBetSlip } from '@/interface/betBuilder';
import type { OpenTicketStatus } from '@/interface/enum';
import {
    AsiaBetPageType,
    BannerPosition,
    BrandType,
    CashOutResult,
    DisplayTime,
    ExchangeStatus,
    FantasySelectionOrderStatus,
    MarketType,
    OddsDisplayMode,
    OddsSorting,
    PriceStyle,
    SiteStyle,
    SportType,
} from '@/interface/enum';
import { JackpotPoolType, TriggerResult } from '@/interface/jackpot';
import type {
    BannerInfo,
    BannerRequest,
    Bet,
    BetBuilderOrderRequest,
    BetBuilderOrderResponse,
    BetBuilderResultResponse,
    BetBuilderTicketRequest,
    BetBuilderTicketResponse,
    BettingPageResponse,
    CashOutRequest,
    CashOutResponse,
    CashOutStatus,
    CashOutValueResponse,
    CashOutValuesRequest,
    ClaimJackpotRequest,
    ConfigurationSettingsParams,
    CustomerInfo,
    DynamicMarketOrderRequest,
    DynamicMarketOrderResponse,
    DynamicMarketResultBySelectionResponse,
    DynamicMarketTicketRequest,
    DynamicMarketTicketResponse,
    DynamicMatchMarketResultResponse,
    ExchangeResponseOfBettingPageResponse,
    ExchangeResponseOfCustomerInfoResponse,
    ExchangeResponseOfObject,
    FantasySelectionBetResponse,
    FantasySelectionMyBetDetailResponse,
    FantasySelectionMyBetsResponse,
    FantasySelectionTokenResponse,
    LiveStreamGetLiveStreamUrlParams,
    LiveStreamResource,
    LiveStreamResponse,
    LocationResponse,
    MatchResponse,
    MatchResultGetBetBuilderResultParams,
    MatchResultGetDynamicMarketResultBySelectionParams,
    MatchResultGetDynamicMarketResultParams,
    MatchResultGetMatchResultsParams,
    MatchResultGetOutrightTournamentResultsParams,
    MaxPayoutResponse,
    MixParlaySubBet,
    MyBetListMiniMyBetsParams,
    MyBetListMyBetDetailParams,
    MyBetListMyBetListParams,
    MyBetListSettledBetList2Params,
    MyBetListSettledBetListParams,
    MyBetsResponse,
    OrderResponse,
    OutrightTicketInfo,
    PlaceComboParlayOrderRequest,
    PlaceFantasySelectionBetRequest,
    PlaceFantasySelectionBetResponse,
    PlaceOutrightOrderRequest,
    PlaceSingleOrderRequest,
    TriggerResult as PolluxTriggerResult,
    RemoveSingleTicket,
    SelectedLeagueInfoRequest,
    SelectedLeagueInfoResponse,
    SendBehaviorsRequest,
    SettingRequest,
    TicketInfo,
    TicketMpResponse,
    TicketResponse,
    TokensResponse,
    TournamentResponse,
    TransferRequest,
    Voucher,
    VoucherGetAllParams,
} from '@/interface/pollux';
import { EnumProject, PolluxPlaceOrderStatus } from '@/interface/pollux';

export type { IDynamicMarketOrder, IDynamicMarketTicket } from '@/core/oddsApi/helpers/formatDynamicMarket';

declare interface IOpenTicketDebug {
    ticket: string;
    duration: number;
    requestTime: Date;
}

export const isWaitingForOpenTicket = ref(false);

export const lastOpenTickets = ref<IOpenTicketDebug[]>([]);

export const mockOpenTicketStatus = ref<OpenTicketStatus | null>(null);

export const mockPlaceBetStatus = ref<PolluxPlaceOrderStatus | null>(null);

export const mockPlaceBetExtraInfo = ref<number | null>(null);

export const mockCashOutResult = ref<CashOutStatus | null>(null);

export const mockCashOutValueIsSupported = ref<boolean | null>(null);

export function createInstance(baseUrl: string) {
    return axios.create({
        baseURL: baseUrl,
        withCredentials: true,
    });
}

export const apiInstance = createInstance(`${getPolluxUrl()}/api`);

function getRequestLabel(config: AxiosRequestConfig) {
    const method = config.method?.toUpperCase();
    const baseURL = config.baseURL ?? apiInstance.defaults.baseURL ?? '';
    const url = config.url ?? '';
    const fullPath = url ? `${baseURL}/${url.replace(/^\/+/, '')}` : baseURL;

    return `${method} ${apiInstance.getUri({ ...config, url: fullPath })}`;
}

function ensureError(error: unknown) {
    if (error instanceof Error) return error;

    return new Error(typeof error === 'string' ? error : JSON.stringify(error));
}

function addRequestContextToError(error: unknown) {
    const normalizedError = ensureError(error);

    if (!axios.isAxiosError(normalizedError) || !normalizedError.config) return normalizedError;

    // Readable only when the server lists it in Access-Control-Expose-Headers.
    const cfRay = normalizedError.response?.headers?.['cf-ray'];
    const cfRayLabel = cfRay ? `, cf-ray: ${cfRay}` : '';

    normalizedError.message = `${normalizedError.message} (request: ${getRequestLabel(normalizedError.config)}${cfRayLabel})`;
    return normalizedError;
}

const goToLoginPage = throttle(() => {
    window.location.href = `${getPolluxUrl()}/api/signin-sbo`;
}, 5000, { trailing: false });

apiInstance.interceptors.response.use(
    (response) => {
        const isUM = response.headers['um-enabled'] === 'y';
        if (isUM) {
            window.location.reload();
        }
        return response;
    },
    (error) => {
        const { response } = error;
        if (response) {
            if (response.status === 401) {
                goToLoginPage();
            }
        }

        return Promise.reject(addRequestContextToError(error));
    },
);

export function fireAndForget(request: Promise<unknown>): void {
    request.catch((error) => {
        console.warn(error);
    });
}

function convertToTicketInfo(ticket: ITicket | ITicketEuro): TicketInfo {
    return {
        eventId: ticket.event.id,
        oddsId: ticket.odds.id,
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        sportType: SportType.toPollux(ticket.odds.sportType),
        isLive: ticket.odds.isLive,
    };
}

function convertToOutrightTicketInfo(ticket: ITicket | ITicketEuro): OutrightTicketInfo {
    return {
        leagueId: ticket.event.league.id,
        eventId: ticket.event.id,
        oddsId: ticket.odds.id,
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        sportType: SportType.toPollux(ticket.odds.sportType),
        isLive: ticket.odds.isLive,
    };
}

function convertToAsiaSubBetRequest(ticket: ITicket): MixParlaySubBet {
    const eventResult = ticket.event.eventResults.find(x => x.id === ticket.odds.eventResult.id);
    return {
        eventId: ticket.event.id,
        oddsId: ticket.odds.id,
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        leagueId: ticket.event.league.id,
        liveScore: {
            home: eventResult?.homeTeam.liveScore ?? 0,
            away: eventResult?.awayTeam.liveScore ?? 0,
        },
        point: ticket.odds.point,
    };
}

function convertToEuroSubBetRequest(ticket: ITicketEuro): MixParlaySubBet {
    return {
        eventId: ticket.event.id,
        oddsId: ticket.odds.id,
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        leagueId: ticket.event.league.id,
        liveScore: {
            home: ticket.liveHomeScore ?? 0,
            away: ticket.liveAwayScore ?? 0,
        },
        point: ticket.odds.point,
    };
}

function handleMockOpenTicketStatus<T extends Pick<TicketResponse, 'errorCode' | 'oddsInfo'>>(response: AxiosResponse<T>) {
    if (mockOpenTicketStatus.value !== null) {
        const mock = mockOpenTicketStatus.value;
        response.data.errorCode = mock;

        if (response.data.oddsInfo) {
            Object.values(response.data.oddsInfo).forEach((oddsInfo) => {
                oddsInfo.errorCode = mock;
            });
        }
    }
    return response;
}

function handleMockPlaceBetStatus<T extends Pick<OrderResponse, 'isPlaceBetSuccess' | 'placeOrderStatus' | 'extraInfo'>>(response: AxiosResponse<T>) {
    if (mockPlaceBetStatus.value) {
        response.data.isPlaceBetSuccess = mockPlaceBetStatus.value === PolluxPlaceOrderStatus.Success;
        response.data.placeOrderStatus = mockPlaceBetStatus.value;
        response.data.extraInfo = mockPlaceBetExtraInfo.value;
    }
    return response;
}

function handleMockCashOutStatus(response: AxiosResponse<CashOutResponse>) {
    if (mockCashOutResult.value !== null) {
        response.data.status = mockCashOutResult.value;
    }
    return response;
}

function handleMockCashOutIsSupported(response: AxiosResponse<CashOutValueResponse[]>) {
    const mockIsSupported = mockCashOutValueIsSupported.value;
    if (mockIsSupported !== null) {
        response.data.forEach((value) => {
            value.isSupported = mockIsSupported;
        });
    }
    return response;
}

function convertToCashOutData(response: AxiosResponse<CashOutValueResponse[]>, isMixParlay: boolean) {
    return response.data.map<ICashOutData>(value => ({
        ...value,
        result: CashOutResult.fromPollux(value.status),
        isMixParlay,
    }));
}

function formatExchangeCustomerInfo(response: BettingPageResponse) {
    return {
        betCredit: response.betCredit,
        hasBettingBudget: response.hasBettingBudget,
        bettingBudget: response.bettingBudget,
        isSuspended: response.isSuspended,
        exchangeBalance: response.exchangeBalance,
        hasDepositBonus: response.hasDepositBonus,
    };
}

const notDefinedType: unique symbol = Symbol('nonExist');
type NotDefinedType = typeof notDefinedType

type TypedParamsAxiosRequestConfig<TParams> = Omit<AxiosRequestConfig, 'params'> & { params?: TParams }

/** wrapper for `axios.get` with request & response type checking */
function get<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    config?: TypedParamsAxiosRequestConfig<TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>>,
): Promise<AxiosResponse<TResponse>> {
    return apiInstance.get<TResponse>(url, config);
}

/** wrapper for `axios.post` with request & response type checking */
function post<TRequest = NotDefinedType, TResponse = NotDefinedType>(
    url: string,
    data?: TRequest extends NotDefinedType ? 'You must provide type parameters' : NoInfer<TRequest>,
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<TResponse>> {
    return apiInstance.post<TResponse>(url, data, config);
}

const openSingleTicket = (ticket: ITicket | ITicketEuro, path: 'singleBetSlip/openTicket' | 'singleBetSlip/openMultipleSingleTicket', cancelToken?: CancelToken) => {
    const request = convertToTicketInfo(ticket);
    const requestTime = new Date();
    isWaitingForOpenTicket.value = true;
    return post<TicketInfo, TicketResponse>(path, request, { cancelToken })
        .then(handleMockOpenTicketStatus)
        .then((response) => {
            const responseTime = new Date();
            lastOpenTickets.value.unshift({
                ticket: ticket.key,
                duration: responseTime.getTime() - requestTime.getTime(),
                requestTime,
            });
            lastOpenTickets.value = lastOpenTickets.value.slice(0, 5);
            return response;
        }).finally(() => {
            isWaitingForOpenTicket.value = false;
        });
};

const openOutrightTicket = (ticket: ITicket | ITicketEuro, path: 'outrightBetSlip/openTicket' | 'outrightBetSlip/openMultipleTicket', cancelToken?: CancelToken) => {
    const request = convertToOutrightTicketInfo(ticket);
    return post<OutrightTicketInfo, TicketResponse>(path, request, { cancelToken })
        .then(handleMockOpenTicketStatus);
};

function convertToAsiaSingleOrderRequest(stake: number, uid: string, ticket: ITicket, voucherId?: string): PlaceSingleOrderRequest {
    const eventResult = ticket.event.eventResults.find(x => x.id === ticket.odds.eventResult.id);

    return {
        stake,
        uid,
        oddsId: ticket.odds.id,
        betPage: ticket.betPage,
        sportType: SportType.toPollux(ticket.odds.sportType),
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        eventId: ticket.event.id,
        liveScore: {
            home: eventResult?.homeTeam.liveScore ?? 0,
            away: eventResult?.awayTeam.liveScore ?? 0,
        },
        point: ticket.odds.point,
        voucherIdString: voucherId ?? '',
    };
}

function convertToEuroSingleOrderRequest(stake: number, uid: string, ticket: ITicketEuro, voucherId?: string): PlaceSingleOrderRequest {
    return {
        stake,
        uid,
        oddsId: ticket.odds.id,
        betPage: ticket.betPage,
        sportType: SportType.toPollux(ticket.odds.sportType),
        marketType: MarketType.toPollux(ticket.odds.marketType),
        option: ticket.priceOption.option,
        eventId: ticket.event.id,
        liveScore: {
            home: ticket.liveHomeScore ?? 0,
            away: ticket.liveAwayScore ?? 0,
        },
        point: ticket.odds.point,
        voucherIdString: voucherId ?? '',
    };
}

function isBrokenTokens(data: TokensResponse): boolean {
    if (typeof data !== 'object' || data === null) return true;
    return !data.authToken || !data.om || !data.sm;
}

export const Api = {
    async getOddsApiTokens() {
        const response = await get<never, TokensResponse>('/v2/oddsApi/getTokens');
        const broken = isBrokenTokens(response.data);
        if (broken) {
            throw new UnexpectedResponseError(`getOddsApiTokens response: ${JSON.stringify(response.data)}`);
        }

        return response;
    },
    getCustomerInfo() {
        return get<never, CustomerInfo>('/account/getCustomerInfo')
            .then((response) => {
                apiInstance.defaults.baseURL = `${getPolluxUrl()}${response.data.basePath ?? '/api'}`;

                return response;
            });
    },
    getLocation() {
        return get<never, LocationResponse>('configuration/location');
    },
    getToggle(siteStyle: SiteStyle) {
        return get<ConfigurationSettingsParams, IToggleResponse>('/configuration/settings', {
            params: {
                project: EnumProject.Kratos,
                siteStyle: SiteStyle.toPollux(siteStyle),
            },
        });
    },
    updateIsAcceptAnyOdds(isAcceptAnyOdds: boolean) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateIsAcceptAnyOdds', { isAcceptAnyOdds });
    },
    updateIsAcceptAnyCashOutValue(isAcceptAnyCashOutValue: boolean) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateIsAcceptAnyCashOutValue', { isAcceptAnyCashOutValue });
    },
    updatePriceStyle(priceStyle: PriceStyle) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updatePriceStyle', {
            priceStyle: PriceStyle.toPollux(priceStyle),
        });
    },
    updateOddsSorting(oddsSorting: OddsSorting) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateOddsSorting', {
            oddsSorting: OddsSorting.toPollux(oddsSorting),
        });
    },
    updateOddsDisplayMode(oddsDisplayMode: OddsDisplayMode) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateOddsDisplayMode', {
            oddsDisplayMode: OddsDisplayMode.toPollux(oddsDisplayMode),
        });
    },
    updateDisplayTime(displayTime: DisplayTime) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateDisplayTime', {
            displayTime: DisplayTime.toPollux(displayTime),
        });
    },
    updatePlaySound(playSound: string) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updatePlaySound', {
            playSound,
        });
    },
    updateSiteStyle(siteStyle: SiteStyle) {
        return post<SettingRequest, CustomerInfo>('/userSetting/updateSiteStyle', {
            siteStyle: SiteStyle.toPollux(siteStyle),
        });
    },
    getVouchers(sportType: SportType, leagueId: number) {
        return get<VoucherGetAllParams, Voucher[]>('/voucher/getAll', {
            params: {
                sportType: SportType.toPollux(sportType),
                leagueId,
            },
        }).then(response => response.data.map<IVoucher>(voucher => ({
            id: voucher.id.toString(),
            stake: voucher.stake,
            expiryDate: parseISOString(voucher.expiryDate),
        })));
    },
    openSingleTicket(ticket: ITicket | ITicketEuro, cancelToken?: CancelToken) {
        return openSingleTicket(ticket, 'singleBetSlip/openTicket', cancelToken);
    },
    openSingleTicketEuro(ticket: ITicket | ITicketEuro, cancelToken?: CancelToken) {
        return openSingleTicket(ticket, 'singleBetSlip/openMultipleSingleTicket', cancelToken);
    },
    openMixParlayTicket(tickets: ITicket[] | ITicketEuro[], cancelToken?: CancelToken) {
        const request = tickets.map(convertToTicketInfo);
        return post<TicketInfo[], TicketMpResponse>('parlayBetSlip/openTicket', request, { cancelToken })
            .then(handleMockOpenTicketStatus);
    },
    openOutrightTicket(ticket: ITicket, cancelToken?: CancelToken) {
        return openOutrightTicket(ticket, 'outrightBetSlip/openTicket', cancelToken);
    },
    openOutrightTicketEuro(ticket: ITicket | ITicketEuro, cancelToken?: CancelToken) {
        return openOutrightTicket(ticket, 'outrightBetSlip/openMultipleTicket', cancelToken);
    },
    async openBetBuilderTicket(request: BetBuilderTicketRequest, cancelToken?: CancelToken) {
        const response = await post<BetBuilderTicketRequest, BetBuilderTicketResponse>('betBuilderBetSlip/openTicket', request, { cancelToken });
        return formatBetBuilderTicket(response.data);
    },
    async openDynamicMarketTicket(selectionId: number, cancelToken?: CancelToken) {
        const request: DynamicMarketTicketRequest = { selectionId };
        const response = await post<DynamicMarketTicketRequest, DynamicMarketTicketResponse>(
            'dynamicMarketBetSlip/openTicket',
            request,
            { cancelToken },
        );
        return formatDynamicMarketTicket(response.data);
    },
    async placeDynamicMarketBet(request: DynamicMarketOrderRequest) {
        const response = await post<DynamicMarketOrderRequest, DynamicMarketOrderResponse>(
            'dynamicMarketBetSlip/placeBet',
            request,
        );
        return formatDynamicMarketOrder(response.data);
    },
    async placeDynamicMarketFreeBet(request: DynamicMarketOrderRequest) {
        const response = await post<DynamicMarketOrderRequest, DynamicMarketOrderResponse>(
            'dynamicMarketBetSlip/placeFreeBet',
            request,
        );
        return formatDynamicMarketOrder(response.data);
    },
    clearTicket() {
        return post('betSlip/clearTicket');
    },
    clearSingleTicket(uid: string) {
        return post<RemoveSingleTicket>('betSlip/clearSingleTicket', { uid });
    },
    placeAsiaSingleBet(betSlip: IBetSlip, stake: number) {
        const ticket = betSlip.tickets[0];
        const request = convertToAsiaSingleOrderRequest(stake, betSlip.uid, ticket);
        return post<PlaceSingleOrderRequest, OrderResponse>('singleBetSlip/placeBet', request)
            .then(handleMockPlaceBetStatus);
    },
    placeEuroAllSingleBets(singleList: IBetSlipEuro[]) {
        const request = singleList.map(betSlip => convertToEuroSingleOrderRequest(betSlip.stake, betSlip.uid, betSlip.tickets[0], betSlip.voucherId));

        return post<PlaceSingleOrderRequest[], OrderResponse[]>('singleBetSlip/placeBets', request);
    },
    placeAsiaFreeBet(betSlip: IBetSlip, stake: number, voucherId: string) {
        const ticket = betSlip.tickets[0];
        const request = convertToAsiaSingleOrderRequest(stake, betSlip.uid, ticket, voucherId);
        return post<PlaceSingleOrderRequest, OrderResponse>('singleBetSlip/placeFreeBet', request)
            .then(handleMockPlaceBetStatus);
    },
    /**
     * Temporarily use this because this response have transId and desktop don't need combo parlay feature yet
     */
    placeAsiaMixParlayBet(betSlip: IBetSlip, stake: number) {
        const request: PlaceComboParlayOrderRequest = {
            stake,
            uid: betSlip.uid,
            mpBet: betSlip.tickets.map(convertToAsiaSubBetRequest),
            selectedCombo: [betSlip.tickets.length],
            betPage: betSlip.tickets[0].betPage,
        };

        return post<PlaceComboParlayOrderRequest, OrderResponse>('parlayBetSlip/placeMixParlay', request)
            .then(handleMockPlaceBetStatus);
    },
    placeEuroMixParlayBet(betSlip: IBetSlipEuro, stake: number) {
        const request: PlaceComboParlayOrderRequest = {
            stake,
            uid: betSlip.uid,
            mpBet: betSlip.tickets.map(convertToEuroSubBetRequest),
            selectedCombo: [betSlip.tickets.length],
            betPage: AsiaBetPageType.MixParlay,
        };

        return post<PlaceComboParlayOrderRequest, OrderResponse>('parlayBetSlip/placeMixParlay', request)
            .then(handleMockPlaceBetStatus);
    },
    placeAsiaOutrightBet(betSlip: IBetSlip, stake: number) {
        const ticket = betSlip.tickets[0];
        const request: PlaceOutrightOrderRequest = {
            stake,
            uid: betSlip.uid,
            betPage: ticket.betPage,
            sportType: SportType.toPollux(ticket.odds.sportType),
            outrightId: ticket.odds.id,
            leagueId: ticket.event.league.id,
            isLive: ticket.event.isLive,
        };
        return post<PlaceOutrightOrderRequest, OrderResponse>('outrightBetSlip/placeBet', request)
            .then(handleMockPlaceBetStatus);
    },
    placeEuroOutrightBets(betSlip: IBetSlipEuro[]) {
        const request: PlaceOutrightOrderRequest[] = betSlip.map((betSlip) => {
            const ticket = betSlip.tickets[0];
            return {
                stake: betSlip.stake,
                uid: betSlip.uid,
                betPage: ticket.betPage,
                sportType: SportType.toPollux(ticket.odds.sportType),
                marketType: MarketType.toPollux(ticket.odds.marketType),
                outrightId: ticket.odds.id,
                leagueId: ticket.event.league.id,
                isLive: ticket.event.isLive,
            };
        });
        return post<PlaceOutrightOrderRequest[], OrderResponse[]>('outrightBetSlip/placeBets', request);
    },
    async placeBetBuilder(betSlip: IBetBuilderBetSlip, stake: number) {
        const [firstTicket] = betSlip.tickets;
        const request: BetBuilderOrderRequest = {
            stake,
            uid: betSlip.uid,
            selections: betSlip.tickets.map(ticket => ({
                id: ticket.selection.id,
                liveScore: betSlip.selectionLiveScores.get(ticket.selection.id) ?? getTicketLiveScore(ticket),
            })),
            betPage: firstTicket.betPage,
        };
        const response = await post<BetBuilderOrderRequest, BetBuilderOrderResponse>('betBuilderBetSlip/placeBet', request);
        return formatBetBuilderOrder(response.data);
    },
    getMiniMyBets(isUseCache: boolean) {
        return get<MyBetListMiniMyBetsParams, Bet[]>('myBetList/miniMyBets', {
            params: {
                c: isUseCache,
            },
        });
    },
    getMyBets(isUseCache: boolean) {
        return get<MyBetListMyBetListParams, MyBetsResponse>('myBetList/myBetList', {
            params: {
                c: isUseCache,
            },
        })
            .then<IMyBets>(x => (
            {
                runningBets: x.data.runningBets?.map(formatBet) ?? [],
                settledBets: x.data.settledBets?.map(formatBet) ?? [],
            }));
    },
    getSettledBetList(winLostDate: string) {
        return get<MyBetListSettledBetListParams, Bet[]>('myBetList/settledBetList', {
            params: {
                winLostDate,
            },
        });
    },
    getCashOutValues(transactionIds: number[]) {
        const request: CashOutValuesRequest = {
            transactionIds,
        };
        return post<CashOutValuesRequest, CashOutValueResponse[]>('cashOut/getValues', request)
            .then(handleMockCashOutIsSupported)
            .then(response => convertToCashOutData(response, false));
    },
    getMpCashOutValues(transactionIds: number[]) {
        const request: CashOutValuesRequest = {
            transactionIds,
        };
        return post<CashOutValuesRequest, CashOutValueResponse[]>('cashOut/getMpValues', request)
            .then(handleMockCashOutIsSupported)
            .then(response => convertToCashOutData(response, true));
    },
    cashOutSingleBet(transactionId: number, cashOutValue: number) {
        const request: CashOutRequest = {
            transactionId,
            cashOutValue,
            fullCashOutValue: cashOutValue,
        };
        return post<CashOutRequest, CashOutResponse>('cashOut/single', request)
            .then(handleMockCashOutStatus);
    },
    cashOutMixParlayBet(transactionId: number, cashOutValue: number) {
        const request: CashOutRequest = {
            transactionId,
            cashOutValue,
            fullCashOutValue: cashOutValue,
        };
        return post<CashOutRequest, CashOutResponse>('cashOut/mixParlay', request)
            .then(handleMockCashOutStatus);
    },
    getBannerFromUmai(position: BannerPosition, brandType: BrandType) {
        const request: BannerRequest = {
            brand: BrandType.toPollux(brandType),
            position: BannerPosition.toPollux(position),
        };
        return post<BannerRequest, BannerInfo[]>('banner/getBanners', request);
    },
    getLiveStream(matchId: number, streamId: number, liveStreamType: LiveStreamResource) {
        return get<LiveStreamGetLiveStreamUrlParams, LiveStreamResponse>('liveStream/getLiveStreamUrl', {
            params: {
                matchId,
                streamId,
                liveStreamType,
            },
        });
    },
    getMyEventIds() {
        return get<never, number[]>('account/myMatchIds');
    },
    async getMatchResults(sportType: SportType, resultDate: string) {
        const response = await get<MatchResultGetMatchResultsParams, MatchResponse[]>('matchResult/getMatchResults', {
            params: {
                sportType: SportType.toPollux(sportType),
                resultDate,
            },
        });
        return formatResults(sportType, response.data);
    },
    async getBetBuilderResults(sportType: SportType, matchId: number) {
        const response = await get<MatchResultGetBetBuilderResultParams, BetBuilderResultResponse>('matchResult/getBetBuilderResult', {
            params: {
                sportType: SportType.toPollux(sportType),
                matchId,
            },
        });
        return formatBetBuilderResults(response.data.marketResults);
    },
    async getDynamicMarketResults(sportType: SportType, matchId: number) {
        const response = await get<MatchResultGetDynamicMarketResultParams, DynamicMatchMarketResultResponse>('matchResult/getDynamicMarketResult', {
            params: {
                sportType: SportType.toPollux(sportType),
                matchId,
            },
        });
        return formatDynamicMarketResults(response.data.results);
    },
    async getDynamicMarketResultBySelection(sportType: SportType, matchId: number, selectionId: number) {
        const response = await get<MatchResultGetDynamicMarketResultBySelectionParams, DynamicMarketResultBySelectionResponse>(
            'matchResult/GetDynamicMarketResultBySelection',
            { params: { sportType: SportType.toPollux(sportType), matchId, selectionId } },
        );
        return { wonSelectionName: response.data.winnerSelectionName ?? null };
    },
    getOutrightResults(sportType: SportType, startDate: string, endDate: string) {
        return get<MatchResultGetOutrightTournamentResultsParams, TournamentResponse[]>('matchResult/getOutrightTournamentResults', {
            params: {
                sportType: SportType.toPollux(sportType),
                startDate,
                endDate,
            },
        })
            .then(response => response.data.map(formatOutrightResults));
    },
    getSelectedLeagueInfos() {
        return get<never, SelectedLeagueInfoResponse[]>('userSetting/getSelectedLeagueInfos');
    },
    updateSelectedLeagueInfos(request: SelectedLeagueInfoRequest[]) {
        return post<SelectedLeagueInfoRequest[]>('userSetting/updateSelectedLeagueInfos', request);
    },
    sendBehaviors(request: SendBehaviorsRequest) {
        return post<SendBehaviorsRequest>('behavior/sendBehaviors', request);
    },
    async getMixParlayMaxPayout() {
        const response = await get<never, MaxPayoutResponse>('maxPayout/mixParlay');
        return response.data;
    },
    getJackpotToken() {
        return get<never, string>('jackpot/token');
    },
    claimJackpot(poolType: JackpotPoolType, round: number) {
        return post<ClaimJackpotRequest, PolluxTriggerResult>('jackpot/claim', {
            poolType: JackpotPoolType.ToPollux(poolType),
            round,
        }).then(response => TriggerResult.FromPollux(response.data));
    },
    async getExchangeBettingInfo() {
        const response = await get<never, ExchangeResponseOfBettingPageResponse>('/exchange/getBettingPage');
        return {
            status: ExchangeStatus.fromPollux(response.data.status),
            customerInfo: response.data.data ? formatExchangeCustomerInfo(response.data.data) : null,
            iframeUrl: response.data.data?.iframeUrl ?? '',
        };
    },
    async exchangeTransferIn(amount: number) {
        const response = await post<TransferRequest, ExchangeResponseOfObject>('/exchange/TransferIn', { amount });
        return { status: ExchangeStatus.fromPollux(response.data.status) };
    },
    async exchangeTransferOut(amount: number) {
        const response = await post<TransferRequest, ExchangeResponseOfObject>('/exchange/TransferOut', { amount });
        return { status: ExchangeStatus.fromPollux(response.data.status) };
    },
    async getExchangeCustomerInfo() {
        const response = await get<never, ExchangeResponseOfCustomerInfoResponse>('/exchange/getCustomerInfo');
        return {
            status: ExchangeStatus.fromPollux(response.data.status),
            customerInfo: response.data.data ? formatExchangeCustomerInfo(response.data.data) : null,
        };
    },
    async getFantasySelectionToken() {
        const response = await get<never, FantasySelectionTokenResponse>('fantasySelection/token/getToken');
        return response.data.token;
    },
    async placeFantasySelectionBet(request: PlaceFantasySelectionBetRequest) {
        const response = await post<PlaceFantasySelectionBetRequest, PlaceFantasySelectionBetResponse>('fantasySelection/betSlip/placeBet', request);
        return FantasySelectionOrderStatus.fromPollux(response.data.placeOrderStatus);
    },
    async placeFantasySelectionBets(request: PlaceFantasySelectionBetRequest[]) {
        const response = await post<PlaceFantasySelectionBetRequest[], PlaceFantasySelectionBetResponse[]>('fantasySelection/betSlip/placeBets', request);
        return response.data.map(x => FantasySelectionOrderStatus.fromPollux(x.placeOrderStatus));
    },
    async getFantasySelectionMyBets() {
        const response = await get<never, FantasySelectionMyBetsResponse>('fantasySelection/myBetList/myBetList');
        return formatFantasySelectionMyBets(response.data);
    },
    async getFantasySelectionMyBetDetail(refNo: string) {
        const response = await get<MyBetListMyBetDetailParams, FantasySelectionMyBetDetailResponse>('fantasySelection/myBetList/myBetDetail', {
            params: {
                refNo,
            },
        });
        return formatMyBetDetail(response.data);
    },
    async getFantasySelectionSettledBetList(winLostDate: string) {
        const response = await get<MyBetListSettledBetList2Params, FantasySelectionBetResponse[]>('fantasySelection/myBetList/settledBetList', {
            params: {
                winLostDate,
            },
        });
        return response.data.map(formatFantasySelectionBet);
    },
    async getBalance() {
        const response = await get<never, number>('account/betCredit');
        return response.data;
    },
};
